import PropTypes from 'prop-types';
import cn from 'classnames';
import React, { useCallback, useMemo } from 'react';
import { useDeepCompareEffect } from 'react-use';
import { Link, useLocation } from 'react-router-dom';

import { __t } from '@mssgme/shared';
import { resolvePathname } from '@mssgme/helpers';
import { useHorizontalTabbedScroll } from '@mssgme/ui';
import { useBasePageColor } from '../../../../hooks';

import styles from './ButtonsMenu.scss';

export const ButtonsMenu = ({ items, interactive, activeCollectionId, theme, onSelect, ...rest }) => {
    const location = useLocation();
    const { root, hasLeftBtn, moveLeft, list, hasRightBtn, moveRight, moveTo } = useHorizontalTabbedScroll(
        items.length
    );
    const { backgroundColor, backgroundDimmed, isLightBG } = useBasePageColor();
    const baseUrl = useMemo(() => resolvePathname(location.pathname, 'c'), [location.pathname]);
    const themeStyle = useMemo(
        () => ({
            button: {
                backgroundColor: theme.backgroundColor,
                color: theme.productsCategoryTextColor || theme.color,
                borderRadius: theme.borderRadius,
            },
            buttonActive: {
                backgroundColor: theme.buttonBackground,
                color: theme.buttonTextColor,
                borderRadius: theme.borderRadius,
            },
            arrowButton: { color: theme.color },
            arrowBg: { color: backgroundColor },
        }),
        [theme, backgroundColor, backgroundDimmed, isLightBG]
    );

    const isTabbed = theme.filterMode === 'tabs';
    const ItemTag = interactive ? Link : 'div';
    const handleClick = useCallback(
        (e) => {
            if (!isTabbed) {
                return;
            }

            e.preventDefault();

            const slug = e.target.dataset?.slug;
            const index = items.findIndex((item) => item.slug === slug);
            const id = items[index]?._id;

            if (id === activeCollectionId) {
                // onSelect(null);
            } else {
                moveTo(index);
                onSelect(id);
            }
        },
        [isTabbed, activeCollectionId, items, moveTo, onSelect]
    );

    useDeepCompareEffect(() => {
        if (items.length) {
            moveTo(0);
            onSelect(items[0]._id);
        }
    }, [items]);

    return (
        <div className={styles.root} ref={root} {...rest}>
            {hasLeftBtn && (
                <div key={`le-${backgroundColor}`} className={styles.controlContainerLeft} onClick={moveLeft}>
                    <div className={styles.control} style={themeStyle.arrowButton}>
                        &larr;
                    </div>
                </div>
            )}

            <ul className={cn(styles.list, hasLeftBtn && styles.clipLeft, hasRightBtn && styles.clipRight)} ref={list}>
                {items.map((col, index) => {
                    const isActive = isTabbed && activeCollectionId === col._id;

                    return (
                        <li
                            key={col._id}
                            data-index={index}
                            className={cn({ [styles.item]: true, [styles.active]: isActive })}
                        >
                            <ItemTag
                                style={isActive ? themeStyle.buttonActive : themeStyle.button}
                                data-slug={col.slug}
                                onClick={handleClick}
                                {...interactive && { to: `${baseUrl}/${col.slug}` }}
                            >
                                {col.default ? __t('all_products') : col.name}
                            </ItemTag>
                        </li>
                    );
                })}
            </ul>

            {hasRightBtn && (
                <div key={`ri-${backgroundColor}`} className={styles.controlContainerRight} onClick={moveRight}>
                    <div className={styles.control} style={themeStyle.arrowButton}>
                        &rarr;
                    </div>
                </div>
            )}
        </div>
    );
};

ButtonsMenu.propTypes = {
    items: PropTypes.array.isRequired,
    interactive: PropTypes.bool,
    activeCollectionId: PropTypes.string,
    theme: PropTypes.object.isRequired,
    onSelect: PropTypes.func,
};
